/**
 * The inspector of an OGraf clip (0.93.0): a preview at the playhead, the form generated from the graphic's schema
 * (in property order: text, choice, colour, image, number and checkbox), step marks and updates at the playhead,
 * a newer-version marker with an explicit update, and a warning when the graphic was authored at another rate.
 */
import { useMemo, useRef, useState } from 'react';
import { ArrowUpCircle, Plus, Trash2, TriangleAlert } from 'lucide-react';
import {
  TICKS_PER_SECOND, aspectLabel, frameRateLabel, ografAuthoredRate, ografFields, ografIsSting, ografLayoutFor, ografLayouts, ografPosterMs, ografStepCount, ografValueFits, ops, sameRate, snap,
  type GraphicData, type GraphicTemplateDTO, type GraphicValue, type OgrafField, type OgrafManifest, type SequenceDocument,
} from '@prism/contracts';
import { useFormatView } from './formatContext';
import { EditingFormat, ShownIn } from './edit-inspector';
import { EditGraphicButton } from '../../graphics/EditGraphicButton';
import { editorDefaultsFor } from '../../graphics/editorDefaults';
import { OgrafPreview, carryToVersion, clipMoment, clipSchedule, fixedLengthTicks, newerVersion } from '../../graphics/ograf';
import { imageUrl, useOgrafPackage, type OgrafPackageData } from '../../graphics/ografPackage';
import { toast } from '../../components/Toasts';
import type { Selection } from './Timeline';

const S = TICKS_PER_SECOND;
type Graphic = SequenceDocument['graphics'][number];

export function OgrafInspector({ doc, graphic, template, templates, apply, setSelection, playhead, setPlayhead, canEdit, tc }: {
  doc: SequenceDocument; graphic: Graphic; template: GraphicTemplateDTO & { ograf: OgrafManifest }; templates: GraphicTemplateDTO[];
  apply: (fn: (d: SequenceDocument) => SequenceDocument) => void; setSelection: (s: Selection) => void; playhead: number; setPlayhead: (t: number) => void;
  canEdit: boolean; tc: (t: number) => string;
}) {
  const m = template.ograf;
  const rate = doc.frameRate;
  const fields = useMemo(() => ografFields(m), [m]);
  const pkg = useOgrafPackage(template.id, template.version);
  const [editing, setEditing] = useState<number | null>(null);
  const schedule = useMemo(() => clipSchedule(m, graphic, rate), [m, graphic, rate]);
  const inClip = playhead >= graphic.recordInTicks && playhead < graphic.recordInTicks + graphic.durationTicks;
  const local = playhead - graphic.recordInTicks;
  // Outside the clip the preview shows the step-0 hold, as the poster does.
  const posterTicks = Math.min(graphic.durationTicks - 1, Math.round((ografPosterMs(m, rate) / 1000) * S));
  const moment = clipMoment(graphic, rate, inClip ? playhead : graphic.recordInTicks + posterTicks);
  const newer = newerVersion(templates, graphic);
  const authored = ografAuthoredRate(m);
  const fixed = fixedLengthTicks(template, rate);
  const sting = ografIsSting(m);
  const steps = ografStepCount(m);
  const sourceAsset = template.asset?.id ?? null;
  const updates = graphic.updates ?? [];
  const marks = graphic.stepMarks ?? [];
  // 1.7.0: the package's layouts (one per aspect) and the one the active format uses; another shape is fitted.
  const frame = useFormatView(doc).activeFormat;
  const layouts = useMemo(() => ografLayouts(m), [m]);
  const { layout, exact } = ografLayoutFor(m, frame);
  const update = editing !== null ? updates[editing] : undefined;
  // What the fields show: the clip's data, or for an update the data in effect just before it plus its own changes.
  const inherited: GraphicData = useMemo(() => {
    let d: GraphicData = { ...graphic.data };
    if (editing !== null) for (const u of updates.slice(0, editing)) d = { ...d, ...u.data };
    return d;
  }, [graphic.data, updates, editing]);

  const setValue = (f: OgrafField, v: GraphicValue) => {
    if (editing === null) { apply((d) => ops.updateGraphic(d, graphic.id, { data: { [f.id]: v } })); return; }
    apply((d) => ops.setGraphicUpdates(d, graphic.id, updates.map((u, i) => (i === editing ? { ...u, data: { ...u.data, [f.id]: v } } : u))));
  };
  const clearValue = (f: OgrafField) => {
    if (editing === null) return;
    apply((d) => ops.setGraphicUpdates(d, graphic.id, updates.map((u, i) => (i === editing ? { ...u, data: Object.fromEntries(Object.entries(u.data).filter(([k]) => k !== f.id)) } : u))));
  };
  const atPlayhead = snap(Math.max(0, local), rate);
  const canMark = canEdit && inClip && atPlayhead > 0;

  function updateVersion(next: GraphicTemplateDTO) {
    const carried = carryToVersion(next, graphic);
    apply((d) => {
      let out = ops.retargetGraphic(d, graphic.id, { templateVersion: next.version, data: carried.data, updates: carried.updates ?? [] });
      const len = fixedLengthTicks(next, d.frameRate);
      if (len) out = ops.updateGraphic(out, graphic.id, { durationTicks: len });
      return out;
    });
    toast(`Updated to version ${next.version}; values of fields that still exist were kept`);
  }

  return (
    <div className="inspector-pane" data-testid="graphic-inspector" data-engine="ograf-v1">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <p className="section-label" style={{ margin: 0 }}>Graphic · {m.name} v{graphic.templateVersion}</p>
        <EditingFormat doc={doc} />
      </div>
      {layouts.length > 1 && (
        <div className="ograf-layouts" data-testid="ograf-layouts" data-used={layout.id}>
          <span className="faint">Layouts</span>
          {layouts.map((l) => (
            <span key={l.index} className={`tag ${l.index === layout.index ? 'on' : ''}`} data-testid="ograf-layout" data-layout={l.id} aria-current={l.index === layout.index ? 'true' : undefined}
              title={`${l.label} · ${l.width}×${l.height}${l.index === 0 ? ' · the primary layout' : ''}`}>{l.label}</span>
          ))}
          <span className="faint" data-testid="ograf-layout-used">{frame.label} uses {layout.label}</span>
        </div>
      )}
      {!exact && (
        <div className="banner info" data-testid="graphic-fitted-note">
          <div className="banner-body">
            {layouts.length > 1 && <strong data-testid="ograf-layout-note">No {aspectLabel(frame)} layout; the {aspectLabel(layout)} layout is fitted.</strong>}
            <span>Authored for {layout.width}×{layout.height}; shown fitted in {frame.width}×{frame.height}.</span>
          </div>
        </div>
      )}
      {newer && (
        <div className="banner warning" data-testid="newer-version-banner">
          <div className="banner-body" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>Version {newer.version} of this graphic is available. This clip stays on v{graphic.templateVersion} until you update it.</span>
            {canEdit && <button className="btn sm" onClick={() => updateVersion(newer)} data-testid="update-graphic-version"><ArrowUpCircle size={13} /> Update to v{newer.version}</button>}
          </div>
        </div>
      )}
      {authored && !sameRate(authored, rate) && (
        <p className="warn-text" style={{ margin: 0, fontSize: 12.5 }} data-testid="frame-rate-warning">
          <TriangleAlert size={12} /> Made for {frameRateLabel(authored)} fps; this sequence is {frameRateLabel(rate)} fps. The graphic follows the sequence, so check its timing.
        </p>
      )}
      {sourceAsset && (
        <EditGraphicButton assetId={sourceAsset} title={template.asset?.title} className="btn ghost sm" testId="clip-edit-graphic" style={{ alignSelf: 'flex-start' }}
          defaults={editorDefaultsFor(doc)} />
      )}
      <div className="graphic-preview-box" data-testid="ograf-inspector-preview">
        <OgrafPreview template={template} data={graphic.data} schedule={schedule} rate={rate} ms={moment.ms} />
      </div>
      <fieldset disabled={!canEdit} className="ograf-fields" style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {updates.length > 0 && (
          <div className="field">
            <label htmlFor="ograf-editing">Editing</label>
            <select id="ograf-editing" className="select" value={editing ?? ''} onChange={(e) => setEditing(e.target.value === '' ? null : Number(e.target.value))} data-testid="ograf-editing">
              <option value="">The clip's values</option>
              {updates.map((u, i) => <option key={i} value={i}>Update at {tc(graphic.recordInTicks + u.atTicks)}</option>)}
            </select>
          </div>
        )}
        {fields.map((f) => (
          <FieldControl key={f.id} f={f} pkg={pkg.data}
            value={editing !== null && update && f.id in update.data ? update.data[f.id] : inherited[f.id] ?? f.default}
            own={editing === null || Boolean(update && f.id in update.data)}
            onChange={(v) => setValue(f, v)} onClear={editing !== null && update && f.id in update.data ? () => clearValue(f) : undefined} />
        ))}
        {!fields.length && <p className="muted" style={{ margin: 0 }}>This graphic has no fields.</p>}

        {!sting && (
          <div className="field" data-testid="step-marks">
            <label>Steps <span className="faint">({steps} step{steps === 1 ? '' : 's'}; each mark plays the next{steps <= 1 ? ', past the last the graphic goes out' : ''})</span></label>
            <div className="ograf-marks">
              {marks.map((t, i) => (
                <div key={i} className="ograf-mark-row">
                  <button type="button" className="btn ghost sm mono" onClick={() => setPlayhead(graphic.recordInTicks + t)}>{tc(graphic.recordInTicks + t)}</button>
                  <span className="faint">step {Math.min(i + 1, Math.max(0, steps - 1))}{i + 1 >= steps ? ' (out)' : ''}</span>
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn ghost icon sm" aria-label="Delete step mark" onClick={() => apply((d) => ops.setStepMarks(d, graphic.id, marks.filter((_, k) => k !== i)))}><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
            <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} disabled={!canMark || marks.includes(atPlayhead)} data-testid="add-step-mark"
              onClick={() => apply((d) => ops.setStepMarks(d, graphic.id, [...marks, atPlayhead]))}
              title={inClip ? undefined : 'Move the playhead into the graphic first'}><Plus size={13} /> Step mark at {tc(playhead)}</button>
          </div>
        )}

        <div className="field" data-testid="graphic-updates">
          <label>Updates <span className="faint">(new values from a moment of the clip)</span></label>
          <div className="ograf-marks">
            {updates.map((u, i) => (
              <div key={i} className={`ograf-mark-row ${editing === i ? 'selected' : ''}`}>
                <button type="button" className="btn ghost sm mono" onClick={() => { setPlayhead(graphic.recordInTicks + u.atTicks); setEditing(i); }}>{tc(graphic.recordInTicks + u.atTicks)}</button>
                <span className="faint" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{Object.keys(u.data).length ? Object.keys(u.data).map((k) => fields.find((f) => f.id === k)?.title ?? k).join(', ') : 'no changes yet'}</span>
                <span style={{ flex: 1 }} />
                <button type="button" className="btn ghost icon sm" aria-label="Delete update" onClick={() => { setEditing(null); apply((d) => ops.setGraphicUpdates(d, graphic.id, updates.filter((_, k) => k !== i))); }}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
          <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} disabled={!canMark || updates.some((u) => u.atTicks === atPlayhead)} data-testid="add-update"
            onClick={() => {
              const next = [...updates, { atTicks: atPlayhead, data: {} }].sort((a, b) => a.atTicks - b.atTicks);
              apply((d) => ops.setGraphicUpdates(d, graphic.id, next));
              setEditing(next.findIndex((u) => u.atTicks === atPlayhead));
            }}><Plus size={13} /> Update at {tc(playhead)}</button>
        </div>

        <div className="field-row">
          <div className="field"><label>Starts</label><span className="mono readonly-value">{tc(graphic.recordInTicks)}</span></div>
          <div className="field"><label htmlFor="gdur">Duration (s)</label>
            <input id="gdur" className="input" type="number" min={0.2} step={0.2} value={(graphic.durationTicks / S).toFixed(2)} readOnly={Boolean(fixed)}
              onChange={(e) => { if (!fixed) apply((d) => ops.updateGraphic(d, graphic.id, { durationTicks: Math.round(Number(e.target.value) * S) })); }} />
          </div>
        </div>
        {fixed && (
          <span className="help" data-testid="sting-length">
            A sting plays in, holds and goes out by itself: its length is {(fixed / S).toFixed(2)} s.
            {graphic.durationTicks !== fixed && canEdit && <> <button type="button" className="btn ghost sm" onClick={() => apply((d) => ops.updateGraphic(d, graphic.id, { durationTicks: fixed }))}>Reset to {(fixed / S).toFixed(2)} s</button></>}
          </span>
        )}
        <ShownIn doc={doc} itemId={graphic.id} hideIn={graphic.hideIn} apply={apply} canEdit={canEdit} />
        <button className="btn ghost sm danger" style={{ alignSelf: 'flex-start' }} onClick={() => { apply((d) => ops.removeClip(d, graphic.id)); setSelection(null); }}><Trash2 size={13} /> Remove graphic</button>
      </fieldset>
      <span className="help">An OGraf graphic, rendered for export frame by frame at the sequence's rate in an isolated browser from this exact version; the preview runs the same code.</span>
    </div>
  );
}

/** One field of the generated form. */
function FieldControl({ f, value, own, pkg, onChange, onClear }: {
  f: OgrafField; value: GraphicValue | undefined; own: boolean; pkg: OgrafPackageData | undefined;
  onChange: (v: GraphicValue) => void; onClear?: () => void;
}) {
  const id = `gf-${f.id}`;
  const thumbs = useRef(new Map<string, string>());
  const [draft, setDraft] = useState<string | null>(null);
  const label = (
    <label htmlFor={id}>
      {f.title}{f.hidden ? <span className="faint"> · not in the label</span> : null}
      {onClear && <button type="button" className="btn ghost sm" style={{ marginLeft: 6, padding: '0 4px' }} onClick={onClear} title="Keep the value from before this update">reset</button>}
    </label>
  );
  const cls = own ? '' : 'ograf-inherited';
  let control: React.ReactNode;
  switch (f.kind) {
    case 'text':
      control = <input id={id} className={`input ${cls}`} maxLength={f.maxLength} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} data-testid={`ograf-field-${f.id}`} />;
      break;
    case 'select':
      control = (
        <select id={id} className={`select ${cls}`} value={String(value ?? '')} onChange={(e) => onChange(f.options!.find((o) => String(o) === e.target.value) ?? e.target.value)} data-testid={`ograf-field-${f.id}`}>
          {f.options!.map((o) => <option key={String(o)} value={String(o)}>{String(o)}</option>)}
        </select>
      );
      break;
    case 'color':
      control = <input id={id} type="color" className={cls} value={String(value ?? '#ffffff')} onChange={(e) => onChange(e.target.value.toLowerCase())} style={{ width: 64, height: 30, padding: 2 }} data-testid={`ograf-field-${f.id}`} />;
      break;
    case 'image':
      control = (
        <div className={`ograf-image-choices ${cls}`} role="radiogroup" aria-labelledby={id} data-testid={`ograf-field-${f.id}`}>
          {f.options!.map((o) => {
            const url = imageUrl(pkg, String(o), thumbs.current);
            return (
              <button key={String(o)} type="button" role="radio" aria-checked={value === o} className={`ograf-image-choice ${value === o ? 'on' : ''}`} title={String(o)} onClick={() => onChange(o)}>
                {url ? <img src={url} alt={String(o).split('/').pop()} /> : <span className="faint" style={{ fontSize: 10 }}>{String(o).split('/').pop()}</span>}
              </button>
            );
          })}
        </div>
      );
      break;
    case 'number': {
      const shown = draft ?? String(value ?? '');
      const ok = (v: number) => ografValueFits(f, v);
      control = (
        <>
          <input id={id} className={`input ${cls}`} type="number" min={f.minimum} max={f.maximum} step={f.integer ? 1 : 'any'} value={shown} data-testid={`ograf-field-${f.id}`}
            onChange={(e) => { const v = Number(e.target.value); setDraft(e.target.value); if (e.target.value !== '' && ok(v)) { onChange(v); setDraft(null); } }}
            onBlur={() => setDraft(null)} />
          {draft !== null && <span className="error-text field-hint">{f.minimum !== undefined || f.maximum !== undefined ? `From ${f.minimum ?? '−∞'} to ${f.maximum ?? '∞'}` : 'A number'}{f.integer ? ', whole numbers' : ''}</span>}
        </>
      );
      break;
    }
    case 'boolean':
      return (
        <label className={`toggles ${cls}`} htmlFor={id}>
          <span><input id={id} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} data-testid={`ograf-field-${f.id}`} /> {f.title}</span>
          {onClear && <button type="button" className="btn ghost sm" onClick={onClear}>reset</button>}
        </label>
      );
    default:
      control = <span className="faint" style={{ fontSize: 12 }}>Set in PRISM Graphics (this kind of field is not edited on the timeline).</span>;
  }
  return <div className="field">{label}{control}{f.description && <span className="help">{f.description}</span>}</div>;
}
